import * as React from "react";
import { CoObjectSummary } from "../interfaces/CoObject";
import { RunResult, RunStatusType } from "./RulesResultPanel";
import { ConsistencyResultContent } from "./ConsistencyResultContent";
import { CustomResultContent } from "./CustomResultContent";
import { TableTotalsResultContent } from "./TableTotalsResultContent";
import { RuleType } from "../interfaces/Rule";
import { formatMessage, formatMessageDefault } from "../../utils/helpers/LanguageHelper";
import {
  CAPTION_RESULT_STATUS_CANCELED,
  CAPTION_RESULT_STATUS_QUEUED,
  CAPTION_RESULT_STATUS_RUNNING,
} from "../document/validation/validationCaptions";

export interface RuleResultContentProps {
  result: RunResult;
  onShowOnDocument?: (payload: CoObjectSummary) => void;
}

export function getPalette(status: RunStatusType) {
  switch (status) {
    case "VERIFIED":
      return {
        statusLabel: formatMessage("cof.validation.result.status.verified"),
        border: "#c7e7c7",
        accent: "#1f7a1f",
        link: "#0a66c2",
        badgeBg: "#22c55e",
        badgeFg: "#ffffff",
        badgeGlyph: "✓",
      };
    case "FAILED":
      return {
        statusLabel: formatMessage("cof.validation.result.status.failed"),
        border: "#f1c0c0",
        accent: "#b3261e",
        link: "#0a66c2",
        badgeBg: "#ef4444",
        badgeFg: "#ffffff",
        badgeGlyph: "✕",
      };
    case "ERROR":
      return {
        statusLabel: formatMessage("cof.base.error"),
        border: "#f1c0c0",
        accent: "#b3261e",
        link: "#0a66c2",
        badgeBg: "#f59e0b",
        badgeFg: "#111827",
        badgeGlyph: "!",
      };
    // getPalette supplies only color tokens + statusLabel here. The concrete indicator shape
    // (RUNNING = ring+dot, QUEUED = half-circle, CANCELED = deny-bar) and label style are derived
    // by the renderer from the status value itself.
    case "RUNNING":
      return {
        statusLabel: formatMessageDefault(CAPTION_RESULT_STATUS_RUNNING, "Running"),
        border: "#e5e7eb",
        accent: "#0a66c2",
        link: "#0a66c2",
        badgeBg: "#0a66c2",
        badgeFg: "#ffffff",
        badgeGlyph: "",
      };
    case "QUEUED":
      return {
        statusLabel: formatMessageDefault(CAPTION_RESULT_STATUS_QUEUED, "Queued"),
        border: "#e5e7eb",
        accent: "#9ca3af",
        link: "#0a66c2",
        badgeBg: "#9ca3af",
        badgeFg: "#ffffff",
        badgeGlyph: "",
      };
    case "CANCELED":
      return {
        statusLabel: formatMessageDefault(CAPTION_RESULT_STATUS_CANCELED, "Canceled"),
        border: "#e5e7eb",
        accent: "#9ca3af",
        link: "#0a66c2",
        badgeBg: "#9ca3af",
        badgeFg: "#ffffff",
        badgeGlyph: "",
      };
    default:
      return {
        statusLabel: formatMessage("cof.base.error"),
        border: "#e5e7eb",
        accent: "#374151",
        link: "#0a66c2",
        badgeBg: "#e5e7eb",
        badgeFg: "#111827",
        badgeGlyph: "•",
      };
  }
}

/** Body content shown in the expanded accordion panel. */
export const RuleResultContent: React.FC<RuleResultContentProps> = ({ result, onShowOnDocument }) => {
  if (result.ruleType === RuleType.TABLE_TOTALS && result.info?.tableTotals) {
    return <TableTotalsResultContent model={result.info.tableTotals} />;
  }

  if (result.ruleType === RuleType.CONSISTENCY || result.info?.consistencyTags) {
    return (
      <ConsistencyResultContent consistencyTags={result.info?.consistencyTags} onShowOnDocument={onShowOnDocument} />
    );
  }

  return <CustomResultContent result={result} />;
};
